import { createTRPCClient, createWSClient, httpLink, splitLink, wsLink } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import type { AppRouter } from "../../../../api/src/trpc/router";
import { runtimeConfig } from "../config/runtime-config";
import { getSessionToken } from "../session/auth-token";
import { getLiveRequestHeaders } from "./live-sync";

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

const wsClient = createWSClient({
  url: getLiveSocketUrl,
  connectionParams: getLiveConnectionParams,
  lazy: { enabled: true, closeMs: 30_000 },
  keepAlive: { enabled: true, intervalMs: 25_000, pongTimeoutMs: 10_000 },
});

export const trpc = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => op.type === "subscription",
      true: wsLink({ client: wsClient, transformer: superjson }),
      false: httpLink({
        url: runtimeConfig.apiUrl,
        transformer: superjson,
        headers({ op }) {
          const token = getSessionToken();
          return {
            ...getLiveRequestHeaders(op),
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          };
        },
      }),
    }),
  ],
});

function getLiveSocketUrl() {
  const url = new URL(runtimeConfig.apiUrl, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

async function getLiveConnectionParams() {
  const token = getSessionToken();
  if (!token) return null;
  const response = await fetch(`${runtimeConfig.apiUrl.replace(/\/trpc\/?$/, "")}/live/ticket`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) return null;
  const body = await response.json() as { ticket?: unknown };
  return typeof body.ticket === "string" ? { ticket: body.ticket } : null;
}
